/**
 * Date Helper Functions
 *
 * This module provides utilities for handling "pure dates" (dates without time components)
 * in a way that preserves the exact date regardless of timezone.
 *
 * CRITICAL: All dates in this application should represent calendar dates (e.g., "2018-04-04")
 * without any time component or timezone conversion. These functions ensure dates are always
 * handled in the local timezone to prevent off-by-one errors.
 */

/**
 * Parses an ISO date string (YYYY-MM-DD) into a Date object in local timezone
 *
 * @param dateString - ISO format date string (e.g., "2018-04-04")
 * @returns Date object representing the exact date in local timezone, or null if invalid
 *
 * Example:
 * parseLocalDate("2018-04-04") => Date object for April 4, 2018 00:00:00 local time
 */
export const parseLocalDate = (dateString: string | null | undefined): Date | null => {
  if (!dateString || typeof dateString !== 'string') return null;

  const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;

  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10) - 1;
  const day = parseInt(match[3], 10);

  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  if (month < 0 || month > 11) return null;
  if (day < 1 || day > 31) return null;

  const date = new Date(year, month, day);

  if (isNaN(date.getTime())) return null;

  return date;
};

/**
 * Converts a Date object to ISO date string (YYYY-MM-DD) using local timezone
 *
 * @param date - Date object to convert
 * @returns ISO date string in format YYYY-MM-DD, or null if invalid
 *
 * Example:
 * toLocalDateString(new Date(2018, 3, 4)) => "2018-04-04"
 *
 * WARNING: Do NOT use date.toISOString().split('T')[0] as it converts to UTC first!
 */
export const toLocalDateString = (date: Date | null | undefined): string | null => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return null;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

/**
 * Parses various date formats from Excel and returns a Date object in local timezone
 *
 * Supported formats:
 * - ISO: "2018-04-04"
 * - ISO with time: "2018-04-04T12:30:00"
 * - DD/MM/YYYY: "04/04/2018"
 * - MM/DD/YYYY: "04/04/2018" (fallback)
 * - Excel serial number (numeric)
 *
 * @param value - Date value from Excel (string, number, or Date)
 * @returns Date object in local timezone, or null if invalid
 */
export const parseExcelDate = (value: any): Date | null => {
  if (!value) return null;

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number') {
    const excelEpoch = new Date(1899, 11, 30);
    const date = new Date(excelEpoch.getTime() + value * 86400000);
    return isNaN(date.getTime()) ? null : date;
  }

  if (typeof value === 'string') {
    if (value.match(/^\d{4}-\d{2}-\d{2}/)) {
      return parseLocalDate(value);
    }

    if (value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const [part1, part2, year] = value.split('/').map(Number);

      const ddmmDate = new Date(year, part2 - 1, part1);
      if (!isNaN(ddmmDate.getTime()) && ddmmDate.getDate() === part1) {
        return ddmmDate;
      }

      const mmddDate = new Date(year, part1 - 1, part2);
      if (!isNaN(mmddDate.getTime())) {
        return mmddDate;
      }
    }
  }

  return null;
};

/**
 * Parses a time value (can be Date or string) and returns a Date object with time in local timezone
 *
 * @param value - Time value (Date object or time string)
 * @returns Date object with time, or null if invalid
 */
export const parseExcelTime = (value: any): Date | null => {
  if (!value) return null;

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'string') {
    if (value.match(/^\d{4}-\d{2}-\d{2}T/)) {
      return parseLocalDate(value);
    }

    if (value.match(/^\d{1,2}:\d{2}/)) {
      const [hours, minutes] = value.split(':').map(Number);
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);
      return date;
    }
  }

  return null;
};

/**
 * Checks if two dates represent the same calendar day (ignoring time)
 *
 * @param date1 - First date
 * @param date2 - Second date
 * @returns true if both dates are the same day
 */
export const isSameDay = (date1: Date | null, date2: Date | null): boolean => {
  if (!date1 || !date2) return false;

  return date1.getFullYear() === date2.getFullYear() &&
         date1.getMonth() === date2.getMonth() &&
         date1.getDate() === date2.getDate();
};

/**
 * Creates a pure date (at midnight local time) from year, month, day
 *
 * @param year - Full year (e.g., 2018)
 * @param month - Month (1-12, will be converted to 0-11 internally)
 * @param day - Day of month (1-31)
 * @returns Date object at midnight local time
 */
export const createLocalDate = (year: number, month: number, day: number): Date => {
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

/**
 * Parses an ISO timestamp string with date and time into a Date object
 *
 * Supported formats:
 * - ISO with T separator: "2018-04-04T10:30:00"
 * - ISO with space separator: "2018-04-04 10:30:00"
 * - ISO with timezone: "2018-04-04T10:30:00Z" or "2018-04-04T10:30:00+00:00"
 *
 * @param timestampString - ISO format timestamp string
 * @returns Date object with date and time preserved, or null if invalid
 *
 * Example:
 * parseTimestamp("2018-04-04T10:30:00") => Date object for April 4, 2018 at 10:30:00
 */
export const parseTimestamp = (timestampString: string | null | undefined): Date | null => {
  if (!timestampString || typeof timestampString !== 'string') return null;

  try {
    const date = new Date(timestampString);

    if (isNaN(date.getTime())) return null;

    return date;
  } catch (e) {
    return null;
  }
};

/**
 * Converts a Date object to ISO timestamp string with date and time
 *
 * @param date - Date object to convert
 * @returns ISO timestamp string in format YYYY-MM-DDTHH:mm:ss, or null if invalid
 *
 * Example:
 * toTimestampString(new Date(2018, 3, 4, 10, 30)) => "2018-04-04T10:30:00"
 */
export const toTimestampString = (date: Date | null | undefined): string | null => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return null;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};
